import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  Home,
  Users,
  BedDouble,
  ShowerHead,
  CookingPot,
  Sofa,
  ThermometerSnowflake,
  Sunrise,
  Flame,
} from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { SectionCta } from "@/components/SectionCta";
import { ASSETS } from "@/data/content";
import { useI18n } from "@/i18n";

// Mini icons paired to each cottage feature (same order as COTTAGE_FEATURES)
const FEATURE_ICONS = [
  Home,
  Users,
  BedDouble,
  ShowerHead,
  CookingPot,
  Sofa,
  ThermometerSnowflake,
  Sunrise,
  Flame,
];

export default function Cottage() {
  const { t } = useI18n();
  const c = t.cottage;
  const COTTAGE_FEATURES = c.features;
  const [lightbox, setLightbox] = useState(null);
  const [slide, setSlide] = useState(0);
  const [dir, setDir] = useState(1);

  const INTERIOR = [
    { src: ASSETS.cottageLiving, alt: "Salon z sofą, jadalnią i widokiem na las" },
    { src: ASSETS.cottageKitchen, alt: "W pełni wyposażona kuchnia z oknem" },
    { src: ASSETS.cottageBedroom, alt: "Sypialnia na antresoli z drewnianymi belkami" },
    { src: ASSETS.cottageDesk, alt: "Miejsce do pracy — biurko przy oknie" },
    { src: ASSETS.cottageInt1, alt: "Stół z kwiatami i widokiem na zieleń za oknem" },
    { src: ASSETS.cottageInt2, alt: "Kącik wypoczynkowy z komodą i biblioteczką" },
    { src: ASSETS.cottageInt3, alt: "Sypialnia z drewnianymi belkami i wiklinową lampą" },
    { src: ASSETS.cottageInt4, alt: "Fotel przy schodach na antresolę" },
    { src: ASSETS.cottageTwin, alt: "Sypialnia z dwoma łóżkami przy oknie z widokiem na las" },
    { src: ASSETS.cottageBedroomGreen, alt: "Sypialnia z lnianymi narzutami i ręcznikami" },
    { src: ASSETS.cottageLoft, alt: "Antresola z dwoma łóżkami i wiklinową lampą" },
    { src: ASSETS.cottageBathroom, alt: "Łazienka z prysznicem, umywalką i pralką" },
    { src: ASSETS.cottageWc, alt: "Toaleta z kabiną prysznicową" },
    { src: ASSETS.cottageLiving2, alt: "Salon z rozkładaną sofą, telewizorem i klimatyzacją" },
    { src: ASSETS.cottageKitchen2, alt: "Aneks kuchenny z płytą indukcyjną, piekarnikiem i czajnikiem" },
    { src: ASSETS.cottageWardrobe, alt: "Otwarta garderoba z wieszakami i półkami" },
  ];

  const PER_PAGE = 4;
  const pages = [];
  for (let i = 0; i < INTERIOR.length; i += PER_PAGE) {
    pages.push(INTERIOR.slice(i, i + PER_PAGE));
  }

  const goToSlide = (i) => {
    setDir(i > slide ? 1 : -1);
    setSlide(i);
  };

  useEffect(() => {
    const t = setTimeout(() => {
      setDir(1);
      setSlide((i) => (i + 1) % pages.length);
    }, 10000);
    return () => clearTimeout(t);
  }, [slide, pages.length]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [lightbox]);

  return (
    <section
      id="domek"
      data-testid="cottage-section"
      className="relative bg-canvas-soft py-14 md:py-20"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-6 md:gap-8 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-6">
            <Reveal>
              <p className="overline mb-6">{c.overline}</p>
              <h2 className="serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-ink">
                {c.headingA} <br /> <span className="italic">{c.headingB}</span>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 md:pt-4">
            <Reveal delay={0.15}>
              <p className="text-ink-muted text-base md:text-lg leading-relaxed [text-wrap:pretty]">
                {c.intro}
              </p>
              <div className="mt-6 space-y-2 text-ink-muted text-base md:text-lg leading-relaxed [text-wrap:pretty]">
                <p>
                  <span className="text-ink font-medium">
                    {c.notFindLabel}
                  </span>{" "}
                  {c.notFind}
                </p>
                <p>
                  <span className="text-ink font-medium">{c.canMeetLabel}</span>{" "}
                  {c.canMeet}
                </p>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Justified gallery — full images, aligned row heights, no cropping */}
        <div className="space-y-4 md:space-y-6 mb-20 md:mb-28">
          {[
            [
              { youtube: "OBGahhPIgzM", alt: c.videoAlt },
              { src: ASSETS.cottageAerialRiver, alt: "Domek nad Wisłą z lotu ptaka" },
            ],
            [
              { src: ASSETS.cottageDining, alt: "Jadalnia z drewnianym stołem i widokiem na taras" },
              { src: ASSETS.cottageAerial, alt: "Domek PrzyStań w ciszy z lotu ptaka wśród zieleni" },
            ],
            [
              { src: ASSETS.cottageGrill, alt: "Taras z grillem i widokiem na zieleń" },
              { src: ASSETS.cottageChairs, alt: "Taras z wygodnymi fotelami pod pergolą" },
            ],
          ].map((row, ri) => (
            <Reveal
              key={ri}
              delay={ri * 0.1}
              className="flex flex-col md:flex-row gap-4 md:gap-6 md:h-[420px] lg:h-[500px] md:justify-center"
            >
              {row.map((img) =>
                img.youtube ? (
                  <div
                    key={img.youtube}
                    data-testid="cottage-video"
                    className="overflow-hidden w-full md:w-auto md:h-full aspect-video bg-ink"
                  >
                    <iframe
                      src={`https://www.youtube.com/embed/${img.youtube}?rel=0`}
                      title={img.alt}
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      className="w-full h-full"
                      style={{ border: 0 }}
                    />
                  </div>
                ) : (
                  <button
                    key={img.src}
                    type="button"
                    onClick={() => setLightbox(img)}
                    aria-label={`Powiększ zdjęcie: ${img.alt}`}
                    className="group overflow-hidden md:h-full cursor-zoom-in"
                  >
                    <img
                      src={img.src}
                      alt={img.alt}
                      loading="lazy"
                      className="w-full h-auto md:w-auto md:h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                    />
                  </button>
                )
              )}
            </Reveal>
          ))}
        </div>

        {/* Interior carousel — 4 photos per page, auto-plays every 10s, dots to navigate */}
        <Reveal className="mb-20 md:mb-28 block">
          <div
            data-testid="interior-carousel"
            className="relative w-full overflow-hidden"
          >
            <AnimatePresence initial={false} custom={dir} mode="popLayout">
              <motion.div
                key={slide}
                custom={dir}
                variants={{
                  enter: (d) => ({ x: d > 0 ? "100%" : "-100%", opacity: 0 }),
                  center: { x: 0, opacity: 1 },
                  exit: (d) => ({ x: d > 0 ? "-100%" : "100%", opacity: 0 }),
                }}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
              >
                {pages[slide].map((img, i) => (
                  <button
                    key={img.src}
                    type="button"
                    onClick={() => setLightbox(img)}
                    data-testid={`interior-slide-${slide}-img-${i}`}
                    aria-label={`Powiększ zdjęcie: ${img.alt}`}
                    className="group aspect-[3/4] overflow-hidden cursor-zoom-in"
                  >
                    <img
                      src={img.src}
                      alt={img.alt}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                    />
                  </button>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Dots */}
          <div
            className="flex items-center justify-center gap-3 mt-8"
            role="tablist"
            aria-label="Galeria wnętrza domku"
          >
            {pages.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goToSlide(i)}
                data-testid={`interior-dot-${i}`}
                aria-label={`Pokaż zestaw zdjęć ${i + 1}`}
                aria-selected={i === slide}
                className={`h-2.5 rounded-full transition-all duration-500 ${
                  i === slide
                    ? "w-8 bg-brand-red"
                    : "w-2.5 bg-ink/20 hover:bg-ink/40"
                }`}
              />
            ))}
          </div>
        </Reveal>

        {/* Feature list */}
        <Stagger className="grid grid-cols-2 md:grid-cols-3 auto-rows-fr gap-px bg-ink/10 border border-ink/10">
          {COTTAGE_FEATURES.map((f, i) => {
            const Icon = FEATURE_ICONS[i];
            return (
              <StaggerItem
                key={f.title}
                className="group relative bg-canvas-soft p-6 md:p-7 flex flex-col gap-4 hover:bg-brand-red transition-colors duration-500 overflow-hidden"
              >
                <Icon
                  className="w-6 h-6 text-brand-red group-hover:text-canvas shrink-0 transition-colors duration-500"
                  strokeWidth={1.3}
                />
                <div className="relative flex-1 min-h-[56px]">
                  {/* opis rezerwuje miejsce — widoczny dopiero po najechaniu */}
                  <p className="text-sm md:text-base leading-relaxed text-canvas opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-out">
                    {f.desc}
                  </p>
                  {/* tytuł nakłada się na opis — znika po najechaniu */}
                  <h3 className="absolute inset-0 serif text-xl md:text-2xl font-light text-ink leading-tight group-hover:opacity-0 transition-opacity duration-300">
                    {f.title}
                  </h3>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>

        {/* CTA — jak do nas dojechać */}
        <Reveal className="mt-14 md:mt-20 flex justify-center">
          <SectionCta to="/kontakt" testid="cottage-cta-dojazd">
            {c.cta}
          </SectionCta>
        </Reveal>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="cottage-lightbox"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setLightbox(null)}
          >
            <div className="absolute inset-0 bg-ink/85 backdrop-blur-sm" />
            <button
              type="button"
              onClick={() => setLightbox(null)}
              data-testid="cottage-lightbox-close"
              aria-label="Zamknij"
              className="absolute top-6 right-6 z-10 text-canvas/80 hover:text-canvas transition-colors"
            >
              <X size={28} strokeWidth={1.4} />
            </button>
            <motion.img
              src={lightbox.src}
              alt={lightbox.alt}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative z-10 max-h-[90vh] max-w-[92vw] object-contain shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
